/**
 * HVAC Editor Page
 *
 * Page component for the visual HVAC system editor.
 * Integrates with IDF store through bidirectional sync.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '@/stores/appStore';
import { useIDFStore } from '@/stores/idfStore';
import {
  HVACEditor,
  useHVACStore,
  useSyncStore,
  useSyncStatus,
  initializeSyncOrchestrator,
  syncGraphToIDF,
  performInitialSync,
  detectHVACSystems,
  generateGraph,
  getDetectionStats,
} from '@/hvac';
import { FileRequiredEmptyState } from './FileRequiredEmptyState';

export function HVACEditorPage() {
  const navigate = useNavigate();
  const projectName = useAppStore((state) => state.projectName);
  const setProjectName = useAppStore((state) => state.setProjectName);

  // HVAC Store
  const nodes = useHVACStore((state) => state.nodes);
  const edges = useHVACStore((state) => state.edges);
  const isDirty = useHVACStore((state) => state.isDirty);
  const clear = useHVACStore((state) => state.clear);
  const markClean = useHVACStore((state) => state.markClean);
  const setNodes = useHVACStore((state) => state.setNodes);
  const setEdges = useHVACStore((state) => state.setEdges);

  // IDF Store
  const idfDocument = useIDFStore((state) => state.document);
  const serialize = useIDFStore((state) => state.serialize);

  // Sync Store
  const syncEnabled = useSyncStore((state) => state.enabled);
  const setSyncEnabled = useSyncStore((state) => state.setEnabled);
  const clearSync = useSyncStore((state) => state.clearSync);
  const syncStatus = useSyncStatus();

  // Detection state
  const [isDetecting, setIsDetecting] = useState(false);
  const [detectionMessage, setDetectionMessage] = useState<string | null>(null);

  // Track timeout for cleanup to prevent memory leaks
  const messageTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track last auto-detected document to prevent duplicate auto-detections
  const lastAutoDetectedDocRef = useRef<string | null>(null);

  // Helper to set message with auto-clear timeout
  const setMessageWithTimeout = useCallback((message: string | null, duration: number) => {
    // Clear any existing timeout
    if (messageTimeoutRef.current) {
      clearTimeout(messageTimeoutRef.current);
      messageTimeoutRef.current = null;
    }
    setDetectionMessage(message);
    if (message !== null) {
      messageTimeoutRef.current = setTimeout(() => {
        setDetectionMessage(null);
        messageTimeoutRef.current = null;
      }, duration);
    }
  }, []);

  /**
   * Auto-detect HVAC systems from IDF
   */
  const handleAutoDetect = useCallback(() => {
    if (!idfDocument) {
      setMessageWithTimeout('No IDF document loaded. Please open an IDF file first.', 3000);
      return;
    }

    // Check current nodes state to avoid stale closure
    const currentNodes = useHVACStore.getState().nodes;
    if (currentNodes.length > 0) {
      const confirmReplace = window.confirm(
        'This will replace the current HVAC graph with detected systems. Continue?'
      );
      if (!confirmReplace) return;
    }

    setIsDetecting(true);
    setDetectionMessage('Detecting HVAC systems...');

    try {
      // Detect HVAC systems from IDF
      const result = detectHVACSystems(idfDocument.objects);
      const stats = getDetectionStats(result);

      if (result.airLoops.length === 0 && result.standaloneComponents.length === 0) {
        setMessageWithTimeout('No HVAC systems found in the IDF document.', 3000);
        setIsDetecting(false);
        return;
      }

      // Generate visual graph
      const graph = generateGraph(result);

      // Clear current graph and load detected systems
      clear();
      clearSync();
      setNodes(graph.nodes);
      setEdges(graph.edges);

      // Update project name if we found air loops
      if (result.airLoops.length > 0 && result.airLoops[0]) {
        setProjectName(`HVAC - ${result.airLoops[0].name}`);
      }

      // Show success message
      const message = `Detected ${String(stats.airLoopCount)} air loop(s), ${String(stats.supplyComponentCount + stats.demandComponentCount)} components, ${String(stats.connectionCount)} connections`;
      setMessageWithTimeout(message, 5000);

      // Show warnings if any
      if (result.warnings.length > 0) {
        console.warn('Detection warnings:', result.warnings);
      }
    } catch (e) {
      const error = e instanceof Error ? e.message : String(e);
      setMessageWithTimeout(`Detection failed: ${error}`, 5000);
    } finally {
      setIsDetecting(false);
    }
  }, [idfDocument, clear, clearSync, setNodes, setEdges, setProjectName, setMessageWithTimeout]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (messageTimeoutRef.current) {
        clearTimeout(messageTimeoutRef.current);
      }
    };
  }, []);

  // Initialize sync orchestrator
  useEffect(() => {
    const cleanup = initializeSyncOrchestrator();
    return cleanup;
  }, []);

  // Perform initial sync when IDF document changes
  useEffect(() => {
    if (idfDocument && syncEnabled) {
      performInitialSync();
    }
  }, [idfDocument, syncEnabled]);

  // Auto-detect HVAC systems when IDF is loaded and graph is empty
  useEffect(() => {
    // Generate a document identifier (use object reference as proxy)
    const docId = idfDocument ? `${String(idfDocument.objects.length)}_${String(Date.now())}` : null;

    // Only auto-detect if:
    // 1. IDF document exists
    // 2. Graph is empty (no existing nodes)
    // 3. Not already detecting
    // 4. Haven't already auto-detected this document
    if (!idfDocument || !docId) {
      return;
    }

    // Check current state to avoid stale closure issues
    const currentNodes = useHVACStore.getState().nodes;
    if (currentNodes.length > 0 || isDetecting) {
      return;
    }

    // Prevent duplicate auto-detections for the same document
    if (lastAutoDetectedDocRef.current === docId) {
      return;
    }
    lastAutoDetectedDocRef.current = docId;

    // Small delay to ensure page is mounted
    const timer = setTimeout(() => {
      // Double-check state hasn't changed during timeout
      const latestNodes = useHVACStore.getState().nodes;
      if (latestNodes.length === 0) {
        handleAutoDetect();
      }
    }, 100);

    return () => { clearTimeout(timer); };
  }, [idfDocument, isDetecting, handleAutoDetect]);

  /**
   * Handle new project
   */
  const handleNew = useCallback(() => {
    if (isDirty) {
      const confirm = window.confirm(
        'You have unsaved changes. Are you sure you want to create a new project?'
      );
      if (!confirm) return;
    }
    clear();
    clearSync();
    setProjectName('Untitled HVAC Project');
  }, [isDirty, clear, clearSync, setProjectName]);

  /**
   * Export HVAC graph as JSON
   */
  const handleExportJSON = useCallback(() => {
    const data = {
      projectName,
      nodes: nodes.map((n) => ({
        id: n.id,
        type: n.data.componentType,
        name: n.data.name,
        position: n.position,
        fields: n.data.fields,
        idfObjectId: n.data.idfObjectId,
      })),
      edges: edges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourcePort: e.sourceHandle,
        targetPort: e.targetHandle,
        nodeName: e.data?.nodeName,
      })),
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${projectName || 'hvac-system'}.json`;
    a.click();
    URL.revokeObjectURL(url);
    markClean();
  }, [projectName, nodes, edges, markClean]);

  /**
   * Export to IDF format (uses sync to generate IDF objects, then serializes)
   */
  const handleExportIDF = useCallback(() => {
    // Ensure all nodes are synced to IDF
    syncGraphToIDF();

    // Serialize the IDF document
    const idfContent = serialize();

    if (!idfContent) {
      alert('No IDF content to export. Add some HVAC components first.');
      return;
    }

    const blob = new Blob([idfContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${projectName || 'hvac-system'}.idf`;
    a.click();
    URL.revokeObjectURL(url);
    markClean();
  }, [projectName, serialize, markClean]);

  /**
   * Toggle sync
   */
  const handleToggleSync = useCallback(() => {
    setSyncEnabled(!syncEnabled);
  }, [syncEnabled, setSyncEnabled]);

  /**
   * Force sync from graph to IDF
   */
  const handleForceSync = useCallback(() => {
    syncGraphToIDF();
  }, []);

  return (
    <div className="flex h-[calc(100dvh-4rem)] flex-col">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 border-b bg-card px-2 py-2 md:gap-4 md:px-4">
        {/* Primary Actions */}
        <div className="flex items-center gap-1 md:gap-2">
          <button
            onClick={handleNew}
            className="rounded-md bg-secondary px-2 py-1.5 text-xs font-medium hover:bg-secondary/80 md:px-3 md:text-sm"
          >
            New
          </button>
          <button
            onClick={handleExportJSON}
            disabled={nodes.length === 0}
            className="rounded-md bg-secondary px-2 py-1.5 text-xs font-medium hover:bg-secondary/80 disabled:opacity-50 md:px-3 md:text-sm"
          >
            <span className="md:hidden">JSON</span>
            <span className="hidden md:inline">Export JSON</span>
          </button>
          <button
            onClick={handleExportIDF}
            disabled={nodes.length === 0}
            className="rounded-md bg-primary px-2 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 md:px-3 md:text-sm"
            title="Export HVAC system as EnergyPlus IDF objects"
          >
            <span className="md:hidden">IDF</span>
            <span className="hidden md:inline">Export IDF</span>
          </button>
        </div>

        <div className="hidden h-6 w-px bg-border md:block" />

        {/* Auto-Detect */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleAutoDetect}
            disabled={!idfDocument || isDetecting}
            className="flex items-center gap-1 rounded-md bg-blue-600 px-2 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50 md:px-3 md:text-sm"
            title="Auto-detect HVAC systems from loaded IDF"
            data-onboarding="hvac-auto-detect"
          >
            {isDetecting ? (
              <>
                <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                <span className="hidden md:inline">Detecting...</span>
              </>
            ) : (
              <>
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span className="hidden md:inline">Auto-Detect</span>
              </>
            )}
          </button>
        </div>

        <div className="hidden h-6 w-px bg-border md:block" />

        {/* Project Name - Full width on mobile, inline on desktop */}
        <div className="flex w-full items-center gap-2 md:w-auto">
          <label htmlFor="hvac-project-name" className="hidden text-sm text-muted-foreground md:inline">
            Project:
          </label>
          <input
            id="hvac-project-name"
            type="text"
            value={projectName}
            onChange={(e) => { setProjectName(e.target.value); }}
            className="min-w-0 flex-1 rounded-md border bg-background px-2 py-1 text-xs md:flex-none md:text-sm"
            placeholder="Project name"
          />
          {isDirty && <span className="text-xs text-yellow-500">Modified</span>}
        </div>

        <div className="hidden h-6 w-px bg-border md:block" />

        {/* Sync Controls */}
        <div className="flex items-center gap-1 md:gap-2">
          <button
            onClick={handleToggleSync}
            className={`rounded-md px-2 py-1.5 text-xs font-medium md:px-3 md:text-sm ${
              syncEnabled
                ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-950 dark:text-green-300 dark:hover:bg-green-900'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
            title={syncEnabled ? 'IDF sync enabled' : 'IDF sync disabled'}
          >
            {syncEnabled ? 'Sync On' : 'Sync Off'}
          </button>
          <button
            onClick={handleForceSync}
            disabled={!syncEnabled || nodes.length === 0}
            className="hidden rounded-md bg-secondary px-2 py-1.5 text-xs font-medium hover:bg-secondary/80 disabled:opacity-50 md:inline-block md:px-3 md:text-sm"
            title="Force sync all nodes to IDF"
          >
            Force Sync
          </button>
        </div>

        <div className="hidden flex-1 md:block" />

        {/* Status - Hidden on mobile */}
        <div className="hidden items-center gap-3 text-sm text-muted-foreground md:flex">
          <span>{nodes.length} components</span>
          <span>{edges.length} connections</span>
          {syncEnabled && (
            <>
              <span className="h-4 w-px bg-border" />
              <span
                className={`flex items-center gap-1 ${
                  syncStatus.isSyncing
                    ? 'text-yellow-600 dark:text-yellow-400'
                    : syncStatus.syncedNodeCount > 0
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-muted-foreground'
                }`}
              >
                {syncStatus.isSyncing ? (
                  <>
                    <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-yellow-500 dark:bg-yellow-400" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <span className="inline-block h-2 w-2 rounded-full bg-green-500 dark:bg-green-400" />
                    {syncStatus.syncedNodeCount} synced
                  </>
                )}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Detection Message */}
      {detectionMessage && (
        <div className={`border-b px-4 py-2 text-sm ${
          detectionMessage.includes('failed') || detectionMessage.includes('No ')
            ? 'bg-destructive/10 text-destructive border-destructive/20'
            : 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800'
        }`}>
          {detectionMessage}
        </div>
      )}

      {/* Sync Error Message */}
      {syncStatus.error && (
        <div className="border-b px-4 py-2 text-sm bg-destructive/10 text-destructive border-destructive/20 flex items-center justify-between">
          <span>Sync error: {syncStatus.error}</span>
          <button
            onClick={() => {
              useSyncStore.setState({ error: undefined });
            }}
            className="ml-2 text-destructive hover:text-destructive/80"
            title="Dismiss"
            aria-label="Dismiss error"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Editor */}
      <div className="flex-1 overflow-hidden">
        {!idfDocument && nodes.length === 0 ? (
          <FileRequiredEmptyState
            reason="no-file"
            title="No Model Loaded"
            message="Load an IDF file to automatically detect HVAC systems, or start designing from scratch."
            action={{
              label: 'Go to Editor',
              onClick: () => { void navigate('/'); },
            }}
          />
        ) : (
          <HVACEditor height="100%" />
        )}
      </div>
    </div>
  );
}
